import React from 'react';
import { IProjectListing } from '../../../../entities-generated/models/project/project-listing';
import { IActionItemProps } from '../../../../framework/components/bwj-listing/entities';
import { BwjListing } from '../../../../framework/components/bwj-listing';
import { ProjectStatus } from '../../../../entities-generated/common/project-status';
import { formatString, isNotNullOrWhitespace } from '../../../../framework/utils';
import { DAY_IN_MIILISECONDS, getUnixTimestamp, getUtcNowUnixTimestamp, HOUR_IN_MILLISECONDS, MINUTE_IN_MILLISECONDS, toDate } from '../../../../framework/utils/date-time';
import { AppRoutes } from '../../../app-routes';

type ProjectAction = (project: IProjectListing) => void;
type ProjectScheduleAction = (project: IProjectListing, isRescheduling: boolean) => void;

export interface IProjectListItemProps {
  project: IProjectListing;
  onEditCustomer: ProjectAction;
  onScheduleAssessment: ProjectScheduleAction;
  onRecordAssessment: ProjectAction;
  onEditAssessment: ProjectAction;
  onCancelAssessment: ProjectAction;
  onManageIncentives: ProjectAction;
  onViewProjectOverview: ProjectAction;
  onMarkCustomerUnresponsive: ProjectAction;
  onMarkCustomerDisinterested: ProjectAction;
  onScheduleInstallation: ProjectScheduleAction;
  onRecordInstallation: ProjectAction;
  onEditInstallation: ProjectAction;
  onCancelInstallation: ProjectAction;
  onViewTransactions: ProjectAction;
}

interface IProjectActionButtons {
  onEditCustomer: IActionItemProps;
  onScheduleAssessment: IActionItemProps;
  onRescheduleAssessment: IActionItemProps;
  onRecordAssessment: IActionItemProps;
  onEditAssessment: IActionItemProps;
  onCancelAssessment: IActionItemProps;
  onManageIncentives: IActionItemProps;
  onViewProjectOverview: IActionItemProps;
  onMarkCustomerUnresponsive: IActionItemProps;
  onMarkCustomerDisinterested: IActionItemProps;
  onScheduleInstallation: IActionItemProps;
  onRescheduleInstallation: IActionItemProps;
  onRecordInstallation: IActionItemProps;
  onEditInstallation: IActionItemProps;
  onCancelInstallation: IActionItemProps;
  onViewTransactions: IActionItemProps;
}
const toActionButtonProps = (props: IProjectListItemProps) => {
  const actions: IProjectActionButtons = {
    onEditCustomer: {
      icon: 'EditContact',
      text: 'View / Edit Customer',
      onClick: props.onEditCustomer,
    },
    onScheduleAssessment: {
      icon: 'ScheduleEventAction',
      text: 'Schedule Assessment',
      onClick: (p: IProjectListing) => props.onScheduleAssessment(p, false),
    },
    onRescheduleAssessment: {
      icon: 'RecurringEvent',
      text: 'Reschedule Assessment',
      onClick: (p: IProjectListing) => props.onScheduleAssessment(p, true),
    },
    onRecordAssessment: {
      icon: AppRoutes.recordCompletedAssessment.icon,
      text: 'Record Assessment',
      onClick: props.onRecordAssessment,
    },
    onEditAssessment: {
      icon: 'Edit',
      text: 'Edit Assessment',
      onClick: props.onEditAssessment,
    },
    onCancelAssessment: {
      icon: 'Cancel',
      text: 'Cancel Assessment',
      onClick: props.onCancelAssessment,
    },
    onManageIncentives: {
      icon: AppRoutes.managePurchaseIncentives.icon,
      text: 'Purchase Incentives',
      onClick: props.onManageIncentives,
    },
    onViewProjectOverview: {
      icon: 'ClipboardList',
      text: 'Project Overview',
      onClick: props.onViewProjectOverview,
    },
    onMarkCustomerUnresponsive: {
      icon: 'VolumeDisabled',
      text: 'Mark Customer Unresponsive',
      onClick: props.onMarkCustomerUnresponsive,
    },
    onMarkCustomerDisinterested: {
      icon: 'HandsFree',
      text: 'Mark Customer Disinterested',
      onClick: props.onMarkCustomerDisinterested,
    },
    onScheduleInstallation: {
      icon: 'ScheduleEventAction',
      text: 'Schedule Installation',
      onClick: (p: IProjectListing) => props.onScheduleInstallation(p, false),
    },
    onRescheduleInstallation: {
      icon: 'RecurringEvent',
      text: 'Reschedule Installation',
      onClick: (p: IProjectListing) => props.onScheduleInstallation(p, true),
    },
    onRecordInstallation: {
      icon: 'DeveloperTools',
      text: 'Record Installation Report',
      onClick: props.onRecordAssessment,
    },
    onEditInstallation: {
      icon: 'Edit',
      text: 'Edit Installation Report',
      onClick: props.onEditInstallation,
    },
    onCancelInstallation: {
      icon: 'Cancel',
      text: 'Cancel Installation',
      onClick: props.onCancelInstallation,
    },
    onViewTransactions: {
      icon: 'Money',
      text: 'Manage Payments',
      onClick: props.onViewTransactions,
    }
  };

  return actions;
}
const getToday = () => {
  const now = new Date();
  const today = getUnixTimestamp(now.getFullYear(), now.getMonth(), now.getDate());
  return today;
}
const getTommorow = () => getToday() + (HOUR_IN_MILLISECONDS * 24);
const getTimeDelta = (timeUtc: number) => {
  const delta = getUtcNowUnixTimestamp() - timeUtc;

  let unitDelta = Math.floor(delta / DAY_IN_MIILISECONDS);
  if(unitDelta >= 1) {
    return unitDelta === 1 ? 'a day ago' : `${unitDelta} days ago`;
  }

  unitDelta = Math.floor(delta / HOUR_IN_MILLISECONDS);
  if(unitDelta >= 1) {
    return unitDelta === 1 ? 'an hour ago' : `${unitDelta} hours ago`;
  }

  unitDelta = Math.floor(delta / MINUTE_IN_MILLISECONDS);
  if(unitDelta >= 1) {
    return unitDelta === 1 ? 'about a minute ago' : `about ${unitDelta} minutes ago`;
  }

  return 'a few seconds ago';
}

interface IProjectBwjListingProps {
  actions: IActionItemProps[];
  heading: string;
  secondaryLine: string;
  teritaryLine?: string;
  footnote?: string;
  data: IProjectListing;
  onClick: (data: IProjectListing) => void;
}
/*
    InstallationCancelled = 13,
    AssessmentCancelled = 4,
    CustomerUnresponsive = 2,
    CustomerDisinterested = 9,
    ProjectRejected = 5,
    InstallationRejected = 14,
    CustomerBanned = 16, */

// installation completed and documented
const handleInstallationCompleted = (props: IProjectBwjListingProps, project: IProjectListing, actions: IProjectActionButtons) =>{
  if(project.status !== ProjectStatus.InstallationCompleted) { return; }

  props.heading = project.customerName;
  props.secondaryLine = project.customerAddress;
  props.footnote = formatString(`Installation completed {d:d-MMMdyyyy}`, {d: toDate(project.installationCompletedDate)}).formattedString;
  props.actions = [
    actions.onEditInstallation,
    actions.onViewProjectOverview,
    actions.onViewTransactions,
    actions.onEditCustomer,
  ];
  props.onClick = actions.onEditInstallation.onClick!;
}
const handleInstallationScheduled_Past = (props: IProjectBwjListingProps, project: IProjectListing, actions: IProjectActionButtons) =>{
  props.heading = project.customerAddress;
  const bestContact = isNotNullOrWhitespace(project.customerPhone)
    ? project.customerPhone : project.customerEmail;
  props.secondaryLine =  `${project.customerName} | ${bestContact}`;
  props.footnote = toDate(project.assessmentScheduledDate).getHours() < 13 ? 'Morning appointment' : 'Afternoon appointment';
  props.actions = [
    actions.onRecordInstallation,
    actions.onRescheduleInstallation,
    actions.onCancelInstallation,
    actions.onViewProjectOverview,
    actions.onViewTransactions,
    actions.onEditCustomer,
  ];
  props.onClick = actions.onRecordInstallation.onClick!;
}
const handleInstallationScheduled_Upcoming = (props: IProjectBwjListingProps, project: IProjectListing, actions: IProjectActionButtons) => {
  const assessmentDate = toDate(project.assessmentScheduledDate);
  const meridian = assessmentDate.getHours() < 13 ? 'AM' : 'PM';
  props.heading = formatString(`{d:d-wwMMMMdyyyy} (${meridian})`, {d: assessmentDate}).formattedString;
  props.secondaryLine = isNotNullOrWhitespace(project.customerPhone)
    ? `${project.customerName} | ${project.customerPhone}`
    : project.customerName;
  props.teritaryLine = project.customerAddress;
  props.footnote = project.awaitingMaterialDeliveryCount === 0
  ? 'All necessary materials procured'
  : `${project.awaitingMaterialDeliveryCount} materials not yet procured`;
  props.actions = [
    actions.onRescheduleInstallation,
    actions.onCancelInstallation,
    actions.onViewProjectOverview,
    actions.onViewTransactions,
    actions.onEditCustomer,
  ];
  props.onClick = actions.onViewProjectOverview.onClick!;
}
// customer's installation was scheduled, but not yet performed, or is currently in progress
const handleInstallationScheduled = (props: IProjectBwjListingProps, project: IProjectListing, actions: IProjectActionButtons) =>{
  if(project.status !== ProjectStatus.InstallationScheduled
    && project.status !== ProjectStatus.InstallationInProgress) { return; }

  const tomorrow = getTommorow();
  if(project.installationScheduledDate < tomorrow) {
    // installation is scheduled for today, or is overdue
    handleInstallationScheduled_Past(props, project, actions);
  } else {
    // installation is scheduled for some future date
    handleInstallationScheduled_Upcoming(props, project, actions);
  }
}
// customer completed a purchase, but the install has not yet been scheduled
const handleInstallationPurchased = (props: IProjectBwjListingProps, project: IProjectListing, actions: IProjectActionButtons) =>{
  if(project.status !== ProjectStatus.InstallationPurchased) { return; }

  props.heading = project.customerName;
  props.secondaryLine = isNotNullOrWhitespace(project.customerPhone)
    ? `${project.customerEmail} | ${project.customerPhone}`
    : project.customerEmail;
  props.footnote = `Requested ${getTimeDelta(project.projectStatusDate)}`;
  props.actions = [
    actions.onScheduleInstallation,
    actions.onCancelInstallation,
    actions.onViewProjectOverview,
    actions.onViewTransactions,
    actions.onEditCustomer,
  ];
  props.onClick = actions.onScheduleInstallation.onClick!;
}
// customer has made all necessary selections, but has not yet made a purchase
const handleProjectPlanned = (props: IProjectBwjListingProps, project: IProjectListing, actions: IProjectActionButtons) =>{
  if(project.status !== ProjectStatus.ProjectPlanned) { return; }

  props.heading = project.customerName;
  props.secondaryLine = project.customerAddress;
  props.footnote = isNotNullOrWhitespace(project.customerPhone)
    ? `${project.customerEmail} | ${project.customerPhone}`
    : project.customerEmail;
  props.actions = [
    actions.onManageIncentives,
    actions.onViewProjectOverview,
    actions.onEditCustomer,
    actions.onMarkCustomerDisinterested,
  ];
  props.onClick = actions.onViewProjectOverview.onClick!;
}
// customer has made some, but not yet all selections needed to make a purchase
const handleProjectInPlanning = (props: IProjectBwjListingProps, project: IProjectListing, actions: IProjectActionButtons) =>{
  if(project.status !== ProjectStatus.ProjectInPlanning) { return; }

  props.heading = project.customerName;
  props.secondaryLine = project.customerAddress;
  props.footnote = `Project configuration ${project.percentOrderConfigurationComplete}% complete`;
  props.actions = [
    actions.onManageIncentives,
    actions.onViewProjectOverview,
    actions.onEditCustomer,
    actions.onMarkCustomerDisinterested,
  ];
  props.onClick = actions.onViewProjectOverview.onClick!;
}
// an assessment has been done for customer and it has been recorded, but customer has not yet made any selections
const handleCatalogPublished = (props: IProjectBwjListingProps, project: IProjectListing, actions: IProjectActionButtons) =>{
  if(project.status !== ProjectStatus.CatalogPublished) { return; }

  props.heading = project.customerName;
  props.secondaryLine = project.customerAddress;
  props.footnote = `Assessment recorded ${getTimeDelta(project.projectStatusDate)}`;
  props.actions = [
    actions.onViewProjectOverview,
    actions.onEditAssessment,
    actions.onEditCustomer,
    actions.onMarkCustomerDisinterested,
  ];
  props.onClick = actions.onViewProjectOverview.onClick!;
}
const handleAssessmentScheduled_Past = (props: IProjectBwjListingProps, project: IProjectListing, actions: IProjectActionButtons) =>{
  props.heading = project.customerAddress;
  const bestContact = isNotNullOrWhitespace(project.customerPhone)
    ? project.customerPhone : project.customerEmail;
  props.secondaryLine =  `${project.customerName} | ${bestContact}`;
  props.footnote = toDate(project.assessmentScheduledDate).getHours() < 13 ? 'Morning appointment' : 'Afternoon appointment';;
  props.actions = [
    actions.onRecordAssessment,
    actions.onRescheduleAssessment,
    actions.onCancelAssessment,
    actions.onMarkCustomerUnresponsive,
    actions.onEditCustomer,
  ];
  props.onClick = actions.onRecordAssessment.onClick!;
}
const handleAssessmentScheduled_Upcoming = (props: IProjectBwjListingProps, project: IProjectListing, actions: IProjectActionButtons) => {
  const assessmentDate = toDate(project.assessmentScheduledDate);
  const meridian = assessmentDate.getHours() < 13 ? 'AM' : 'PM';
  props.heading = formatString(`{d:d-wwMMMMdyyyy} (${meridian})`, {d: assessmentDate}).formattedString;
  props.secondaryLine = isNotNullOrWhitespace(project.customerPhone)
    ? `${project.customerName} | ${project.customerPhone}`
    : project.customerName;
  props.footnote = project.customerAddress;
  props.actions = [
    actions.onRescheduleAssessment,
    actions.onCancelAssessment,
    actions.onEditCustomer,
  ];
  props.onClick = actions.onRescheduleAssessment.onClick!;
}
// customer requested a measurement, and that measurement was scheduled, but not yet performed
const handleAssessmentScheduled = (props: IProjectBwjListingProps, project: IProjectListing, actions: IProjectActionButtons) =>{
  if(project.status !== ProjectStatus.AssessmentScheduled) { return; }

  const tomorrow = getTommorow();
  if(project.assessmentScheduledDate < tomorrow) {
    // the scheduled measurement is planned for today, or is overdue
    handleAssessmentScheduled_Past(props, project, actions);
  } else {
    // the measurement appointment is for some future date
    handleAssessmentScheduled_Upcoming(props, project, actions);
  }
}
// customer has requested a measurement, but it has not yet been scheduled
const handleAssessmentRequested = (props: IProjectBwjListingProps, project: IProjectListing, actions: IProjectActionButtons) =>{
  if(project.status !== ProjectStatus.AssessmentRequested) { return; }

  props.heading = project.customerName;
  props.secondaryLine = isNotNullOrWhitespace(project.customerPhone)
    ? `${project.customerEmail} | ${project.customerPhone}`
    : project.customerEmail;
  props.footnote = `Requested ${getTimeDelta(project.projectStatusDate)}`;
  props.actions = [
    actions.onScheduleAssessment,
    actions.onMarkCustomerUnresponsive,
  ];
  props.onClick = actions.onScheduleAssessment.onClick!;
}
const getBwjListingProps = (project: IProjectListing, actions: IProjectActionButtons) => {
  const props: IProjectBwjListingProps = {
    actions: [],
    heading: '',
    secondaryLine: '',
    data: project,
    onClick: () => {}
  };

  handleAssessmentRequested(props, project, actions);
  handleAssessmentScheduled(props, project, actions);
  handleCatalogPublished(props, project, actions);
  handleProjectInPlanning(props, project, actions);
  handleProjectPlanned(props, project, actions);
  handleInstallationPurchased(props, project, actions);
  handleInstallationScheduled(props, project, actions);
  handleInstallationCompleted(props, project, actions);

  return props;
}

export const ProjectListItem: React.FunctionComponent<IProjectListItemProps> = (props) => {
  const actions = toActionButtonProps(props);
  const listingProps = getBwjListingProps(props.project, actions);

  return (
    <BwjListing {...listingProps} />
  );
};
